import React, { Component } from "react";
import cn from "classnames";
import { Route, Link } from "react-router-dom";
import { FormattedDate, FormattedMessage } from "react-intl";
import ReactGA from "react-ga4";
import store from "store";

import routing from "../../routing";

import { isMobile } from "../../../common/parameters";
import Seo from "../../../common/helpers/seo";
import getPlanSeo from "../../../common/helpers/getPlanSeo";

import NotFoundContent from "../../components/NotFoundContent";
import Loader, { LoaderWrapper } from "../../components/Loader";
import Retry from "../../components/Retry";

import Tasks from "./Tasks";
import Meta from "./Meta";
import UpperRow from "./UpperRow";
import MiddleRow from "./MiddleRow";
import Menu from "./Menu";
import Scores from "./ScoresTab";
import GenerateTasks from "./GenerateTasks";
import PromoModal from "./PromoModal";
import ModeratorBar from "./ModeratorBar";
import ExperienceEditor from "./ExperienceEditor";

import { StyledPlan, Wrapper, ContentWrapper, StyledLoader, Listed, Promo } from "./styles";

import messages from "./messages";
import { IProps, IState } from "./interfaces";
import { publicPlanViewed } from "./utils";

class Plan extends Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        const id = props.match.params.id;
        this.state = {
            stop: false,
            publicView:
                props.history.location.pathname.slice(1, routing.publicPlan.length + 1) === routing.publicPlan ||
                props.history.location.pathname.slice(1, routing.testModeration.length + 1) === routing.testModeration,
            moderatorView:
                props.history.location.pathname.slice(1, routing.testModeration.length + 1) === routing.testModeration,
            planId: id || "",
            generateTasksId: null,
            showPromo: false,
            notFoundModal: false,
            showTutorial: false,
        };
        if (!process.env.WEBPACK) {
            return;
        }
        let { plan } = props;
        if (id.length !== 24) {
            this.state = {
                ...this.state,
                notFoundModal: true,
                stop: false,
            }
            return;
        }
        if (plan && plan._id !== id) {
            this.state = {
                ...this.state,
                stop: true,
            }
        }
        this.planChange(props);
    }
    promoTimer?: ReturnType<typeof setTimeout>;
    componentDidMount() {
        const { user } = this.props;
        const notLoggedIn = !user || !user.loggedIn;
        if (notLoggedIn) {
            if (isMobile) {
                this.promoTimer = setTimeout(this.showPromo, 10000);
            }
            document.addEventListener("mouseleave", this.showPromo);
        }
        if (this.state.moderatorView) {
            this.props.getModerationProcess(this.state.planId);
        }
        store.set("play-plan-id", this.state.planId);
        if (this.state.publicView) {
            publicPlanViewed(this.state.planId, this.props.post);
        }
    }
    componentWillUnmount() {
        clearTimeout(this.promoTimer);
        document.removeEventListener("mouseleave", this.showPromo);
    }
    showPromo = () => {
        if (window.promoShowed) return;
        window.promoShowed = true;
        if (window.ga && window.ga.create) {
            ReactGA.send({ hitType: "pageview", page: "/promo-modal" });
        }
        clearTimeout(this.promoTimer);
        document.removeEventListener("mouseleave", this.showPromo);
        this.setState({ showPromo: true });
    };
    planChange = (props: IProps) => {
        if (!props) props = this.props;
        const id = props.match.params.id;
        let { plan } = props;
        if (plan && plan._id !== id) {
            props.resetPlan(() => {
                setTimeout(this.getPlan);
            });
        } else {
            setTimeout(this.getPlan);
        }
    };
    componentDidUpdate() {
        if (!this.props.plan || !this.props.plan._id) return;
        if (this.props.plan._id !== this.props.match.params.id) {
            this.planChange(this.props);
        }
    }
    closePromoModal = () => {
        this.setState({ showPromo: false });
        if (window.ga && window.ga.create) {
            ReactGA.send({ hitType: "pageview", page: "/promo-modal-closed" });
        }
    };
    getPlan = () => {
        const { plan, getPlan, history } = this.props;
        if (!plan || (plan as {
            loading?: boolean,
            error?: boolean,
            notFound?: boolean,
        }).error) {
            getPlan(history.location.pathname, res => {
                const { data } = res;
                const publicView = !data.plan.packName;
                this.setState({
                    planId: data.plan._id,
                    publicView,
                });
                if (!data.found) {
                    this.setState({ notFoundModal: true });
                }
            });
        } else {
            this.setState({
                planId: plan._id,
            });
        }

        this.setState({
            notFoundModal: false,
            stop: false,
        });
    };
    generateTasks = (_id: string) => {
        const {
            plan: { _id: planId },
        } = this.props;
        this.props.history.replace(`/${routing.plan}/${planId}`);
        this.setState({ generateTasksId: _id });
    };

    render() {
        const {
            publicView,
            notFoundModal,
            showTutorial,
            stop,
            planId,
            generateTasksId,
            showPromo,
            moderatorView,
        } = this.state;
        const {
            plan,
            user,
            intl: { formatMessage },
            intl,
            history,
        } = this.props;
        if (notFoundModal || plan?.notFound) {
            return (
                <StyledPlan marginTop={50} className={cn("spring--shadow")} style={{ paddingTop: 0 }}>
                    <Seo title={formatMessage(messages.notFound)} type="article" noindex nofollow />
                    <NotFoundContent />
                    {/* <NotFound close={() => this.props.history.goBack()}/> */}
                </StyledPlan>
            );
        }
        if (plan === null || plan === undefined || stop || !plan._id || (planId !== plan._id && process.env.WEBPACK)) {
            return (
                <StyledPlan marginTop={50} className={cn("spring--shadow", "spring--padding")}>
                    <StyledLoader>
                        <LoaderWrapper>
                            {plan && (plan as {
                                loading?: boolean,
                                error?: boolean,
                                notFound?: boolean,
                            }).error ? <Retry onClick={this.getPlan} /> : <Loader />}
                        </LoaderWrapper>
                    </StyledLoader>
                </StyledPlan>
            );
        }
        const type = moderatorView ? routing.testModeration : publicView ? routing.publicPlan : routing.plan;
        const notLoggedIn = !user || !user.loggedIn;
        const planSeo = getPlanSeo(plan, intl, publicView, history.location.pathname);
        return (
            <StyledPlan
                key={plan._id}
                marginTop={50}
                className={cn("spring--shadow", "spring--padding")}
            >
                <Seo {...planSeo} />
                <Wrapper moderatorView={!!moderatorView}>
                    <UpperRow
                        plan={plan}
                        publicView={publicView}
                        notLoggedIn={notLoggedIn}
                        showTutorial={showTutorial}
                        toggleTutorial={state => {
                            this.setState({ showTutorial: state })
                        }}
                        user={user}
                        moderatorView={moderatorView}
                    />
                    {/* {publicView && !moderatorView && (
                        <TopAdSense>
                            <AdSense slot="PublicView_Top" height={90} />
                        </TopAdSense>
                    )} */}
                    <MiddleRow plan={plan} publicView={publicView} user={user} />
                    <Menu
                        plan={plan}
                        type={type}
                        publicView={publicView}
                        notLoggedIn={notLoggedIn}
                    />
                    <ContentWrapper>
                        <Route
                            exact
                            path={`/${type}/:id`}
                            render={() => (
                                <Tasks
                                    moderatorView={moderatorView}
                                    publicView={publicView}
                                    plan={plan}
                                />
                            )}
                        />
                        <Route
                            exact
                            path={`/${type}/:id/${routing.scores}`}
                            render={() => (
                                <Scores plan={plan} generateTasks={this.generateTasks} />
                            )}
                        />
                        <Route
                            exact
                            path={`/${type}/:id/${routing.experience}`}
                            render={() => (
                                <ExperienceEditor plan={plan} publicView={publicView} />
                            )}
                        />
                        <Route
                            path={`/${type}/:id/${routing.meta}`}
                            render={() => <Meta plan={plan} publicView={publicView} type={type} />}
                        />
                        {publicView && (
                            <Listed>
                                <div>
                                    <FormattedMessage {...messages.listed} />
                                    {": "}
                                    {new Date(plan.publicDate!).getFullYear()}.{" "}
                                    <FormattedDate value={new Date(plan.publicDate!)} month="long" />{" "}
                                    {new Date(plan.publicDate!).getDate()}.
                                </div>
                            </Listed>
                        )}
                        {publicView && notLoggedIn && (
                            <Promo>
                                <p>
                                    <FormattedMessage {...messages.promoQuestion} />
                                </p>
                                <Link to={`/${routing.signup}`} className="button">
                                    <span className="pulsate">
                                        <FormattedMessage {...messages.start} />
                                    </span>
                                </Link>
                                <Link to="/" className="button button--light">
                                    <FormattedMessage {...messages.moreInfo} />
                                </Link>
                            </Promo>
                        )}
                    </ContentWrapper>
                </Wrapper>
                {generateTasksId && (
                    <GenerateTasks
                        _id={generateTasksId}
                        plan={plan}
                        close={() => this.setState({ generateTasksId: null })}
                    />
                )}
                {showPromo && <PromoModal close={this.closePromoModal} />}
                {moderatorView && <ModeratorBar />}
            </StyledPlan>
        );
    }
}

export default Plan;
